/**
 * A count per month, as columns. Clicking a column selects that month.
 *
 * Report-agnostic: any report with a `{month, value}` series can use it. One series,
 * so no legend — the card title says what is plotted. Only the selected column is
 * directly labelled; the axis carries the rest.
 *
 * Transplanted from the reference implementation. Hand-written SVG rather than an
 * ECharts adapter, deliberately: the mark *is* the hit target here plus a wider
 * invisible band, which is the behaviour the portal's ECharts wrapper had to
 * reimplement with a zrender plot-band handler (`docs/PROGRESS.md`). Rewriting
 * this against ECharts would trade a working chart for a second implementation
 * of the same idea.
 */

import { useState } from 'react'

import { monthLabel, monthShort } from './format'
import { useElementWidth } from './useElementWidth'

const HEIGHT = 240
const PAD = { top: 18, right: 12, bottom: 34, left: 40 }
const MAX_BAR = 24
const RADIUS = 4

export interface MonthPoint {
  month: string
  value: number
}

interface Props {
  data: readonly MonthPoint[]
  selectedMonth: string | null
  onSelectMonth: (month: string) => void
  /** Singular/plural noun for the tooltip, e.g. ["CAR", "CARs"]. */
  noun: [string, string]
  emptyLabel?: string
}

/**
 * A column with a 4px rounded cap and square corners at the baseline.
 *
 * `n()` is only here to satisfy `restrict-template-expressions`, which refuses an
 * implicit number-to-string in a template — reasonable in general, and pure noise
 * in an SVG path where every value is a coordinate.
 */
function columnPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(RADIUS, h, w / 2)
  const n = (value: number) => value.toString()
  return [
    `M${n(x)},${n(y + h)}`,
    `L${n(x)},${n(y + r)}`,
    `Q${n(x)},${n(y)} ${n(x + r)},${n(y)}`,
    `L${n(x + w - r)},${n(y)}`,
    `Q${n(x + w)},${n(y)} ${n(x + w)},${n(y + r)}`,
    `L${n(x + w)},${n(y + h)}`,
    'Z',
  ].join(' ')
}

function ticksFor(max: number): number[] {
  const target = 4
  const raw = Math.max(1, max) / target
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10
  const ticks: number[] = []
  for (let v = 0; v <= max + step * 0.001; v += step) ticks.push(v)
  return ticks
}

export function MonthColumnChart({
  data,
  selectedMonth,
  onSelectMonth,
  noun,
  emptyLabel = 'No data for these filters.',
}: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const [hovered, setHovered] = useState<number | null>(null)

  if (data.length === 0) return <div className="state">{emptyLabel}</div>

  const chartWidth = Math.max(width, 320)
  const plotWidth = chartWidth - PAD.left - PAD.right
  const plotHeight = HEIGHT - PAD.top - PAD.bottom

  const ticks = ticksFor(Math.max(...data.map((d) => d.value)))
  // `ticksFor` always returns at least [0], so the fallback is unreachable —
  // it exists because the compiler cannot know that, not because zero is a
  // sensible scale.
  const scaleMax = ticks[ticks.length - 1] ?? 1

  const band = plotWidth / data.length
  const barWidth = Math.min(MAX_BAR, band - 8)
  const yOf = (v: number) => PAD.top + plotHeight - (v / scaleMax) * plotHeight

  const labelEvery = Math.ceil((data.length * 34) / Math.max(plotWidth, 1))
  // The index is carried alongside the point rather than re-read through a
  // non-null assertion, so the tooltip's position and its contents cannot
  // disagree about which column is hovered.
  const hoveredPoint = hovered === null ? undefined : data[hovered]
  const active =
    hovered === null || hoveredPoint === undefined
      ? null
      : { index: hovered, point: hoveredPoint }

  return (
    <div className="chart-wrap" ref={ref}>
      <svg
        width={chartWidth}
        height={HEIGHT}
        role="img"
        aria-label={data.map((d) => `${monthLabel(d.month)}: ${String(d.value)}`).join(', ')}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              x2={chartWidth - PAD.right}
              y1={yOf(tick)}
              y2={yOf(tick)}
              stroke={tick === 0 ? 'var(--baseline)' : 'var(--gridline)'}
              strokeWidth={1}
            />
            <text
              x={PAD.left - 8}
              y={yOf(tick) + 4}
              textAnchor="end"
              fontSize={11}
              fill="var(--text-muted)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {tick}
            </text>
          </g>
        ))}

        {data.map((d, i) => {
          const isSelected = d.month === selectedMonth
          const x = PAD.left + band * i + (band - barWidth) / 2
          const y = yOf(d.value)
          const h = PAD.top + plotHeight - y

          return (
            <g key={d.month}>
              <path
                className="bar-rect"
                d={columnPath(x, y, barWidth, h)}
                fill={isSelected ? 'var(--series-1)' : 'var(--seq-200)'}
                onClick={() => {
                  onSelectMonth(d.month)
                }}
              />
              {/* Hit target wider than the mark, so hovering is forgiving. */}
              <rect
                x={PAD.left + band * i}
                y={PAD.top}
                width={band}
                height={plotHeight}
                fill="transparent"
                className="bar-rect"
                onMouseEnter={() => {
                  setHovered(i)
                }}
                onMouseLeave={() => {
                  setHovered(null)
                }}
                onClick={() => {
                  onSelectMonth(d.month)
                }}
              />
              {isSelected && (
                <text
                  x={x + barWidth / 2}
                  y={y - 7}
                  textAnchor="middle"
                  fontSize={12}
                  fontWeight={600}
                  fill="var(--text-primary)"
                >
                  {d.value}
                </text>
              )}
              {(i % labelEvery === 0 || isSelected) && (
                <text
                  x={PAD.left + band * i + band / 2}
                  y={HEIGHT - PAD.bottom + 16}
                  textAnchor="middle"
                  fontSize={11}
                  fill={isSelected ? 'var(--text-primary)' : 'var(--text-muted)'}
                >
                  {monthShort(d.month)}
                </text>
              )}
              {(i === 0 || d.month.endsWith('-01')) && (
                <text
                  x={PAD.left + band * i + band / 2}
                  y={HEIGHT - PAD.bottom + 29}
                  textAnchor="middle"
                  fontSize={10}
                  fill="var(--text-muted)"
                >
                  {d.month.slice(0, 4)}
                </text>
              )}
            </g>
          )
        })}
      </svg>

      {active !== null && (
        <div
          className="tooltip"
          style={{
            left: PAD.left + band * active.index + band / 2,
            top: yOf(active.point.value),
          }}
        >
          <div className="tooltip-title">{monthLabel(active.point.month)}</div>
          <div className="tooltip-value">
            {active.point.value} {active.point.value === 1 ? noun[0] : noun[1]}
          </div>
        </div>
      )}
    </div>
  )
}
